import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Spinner, Badge, Form, Alert } from 'react-bootstrap';
import { fetchArticles, fetchArticleById, fetchCategories, deleteArticle, updateCategory, fetchQuestionInsights, clearCurrentArticle, fetchConversations } from '../../redux/knowledgeBaseSlice';
import ArticleEditorModal from './ArticleEditorModal';
import CategoryEditorModal from './CategoryEditorModal';
import QuestionInsights from './QuestionInsights';
import { toDisplayList, indentedLabel, isActiveNode } from './categoryTree';
import { useGetApplicationsQuery, useGetRolesQuery } from '../../redux/accessSlice';
import { summarizeCategory } from './visibilityState';

// Thunk rejections carry a normalised { message } object, or for a few older
// thunks a bare string. Read either.
const errorText = (payload, fallback) =>
  (typeof payload === 'string' ? payload : payload?.message) || fallback;

const AdminTab = () => {
  const dispatch = useDispatch();
  const {
    articles, adminCategories, categoriesError, currentArticle, loading, pagination, adminQuestions, adminConversations,
  } = useSelector(state => state.knowledgeBase);

  const [activeSubTab, setActiveSubTab] = useState('articles');
  const [editingArticle, setEditingArticle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [prefillTitle, setPrefillTitle] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  // Names for the "Visible to" summary on top-level categories (WEB-829).
  const { data: applications = [], isLoading: appsLoading, isError: appsError } = useGetApplicationsQuery();
  const { data: roles = [], isLoading: rolesLoading, isError: rolesError } = useGetRolesQuery();
  const audienceLabel = (category) => {
    if (appsLoading || rolesLoading) return 'loading…';
    if (appsError || rolesError) return 'names unavailable';
    return summarizeCategory(category, applications, roles);
  };

  // The admin view needs hidden categories too. They land in their own store
  // slot (adminCategories) so a public fetch from Browse can never clobber them.
  const reloadCategories = useCallback(
    () => dispatch(fetchCategories({ includeInactive: true })),
    [dispatch]
  );

  const reloadArticles = useCallback(
    () => dispatch(fetchArticles({ categoryId: categoryFilter || undefined, search: searchFilter || undefined, page, limit: 25 })),
    [dispatch, categoryFilter, searchFilter, page]
  );

  useEffect(() => {
    reloadCategories();
  }, [reloadCategories]);

  useEffect(() => {
    if (activeSubTab === 'insights') {
      dispatch(fetchQuestionInsights());
      dispatch(fetchConversations());
    }
  }, [dispatch, activeSubTab]);

  useEffect(() => {
    const timer = setTimeout(reloadArticles, searchFilter ? 400 : 0);
    return () => clearTimeout(timer);
  }, [reloadArticles, searchFilter]);

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    const result = await dispatch(deleteArticle(articleId));
    if (deleteArticle.rejected.match(result)) {
      window.alert(errorText(result.payload, 'Failed to delete article.'));
    }
    reloadArticles();
    reloadCategories(); // keeps the per-category article counts current
  };

  const handleSaved = () => {
    setEditingArticle(null);
    setIsCreating(false);
    setPrefillTitle('');
    dispatch(clearCurrentArticle());
    reloadArticles();
    reloadCategories();
  };

  const gapCount = adminQuestions.filter(q => !q.had_good_match).length;

  // Full tree including hidden categories, in display order.
  const categoryList = useMemo(() => toDisplayList(adminCategories), [adminCategories]);

  const handleToggleActive = async (category) => {
    const result = await dispatch(updateCategory({
      categoryId: category.category_id,
      data: { isActive: !isActiveNode(category) },
    }));
    if (updateCategory.rejected.match(result)) {
      window.alert(errorText(result.payload, 'Failed to update category.'));
    }
    reloadCategories();
  };

  const handleCategorySaved = () => {
    setEditingCategory(null);
    setIsCreatingCategory(false);
    reloadCategories();
  };

  return (
    <div>
      {categoriesError && (
        <Alert variant="danger" className="py-2" style={{ fontSize: '13px' }}>
          Could not load categories. {categoriesError}
        </Alert>
      )}

      {/* Sub-tabs */}
      <div className="d-flex gap-2 mb-3 align-items-center">
        <Button
          variant={activeSubTab === 'articles' ? 'dark' : 'outline-secondary'}
          size="sm"
          onClick={() => setActiveSubTab('articles')}
        >
          Articles
        </Button>
        <Button
          variant={activeSubTab === 'categories' ? 'dark' : 'outline-secondary'}
          size="sm"
          onClick={() => setActiveSubTab('categories')}
        >
          Categories
        </Button>
        <Button
          variant={activeSubTab === 'insights' ? 'dark' : 'outline-secondary'}
          size="sm"
          onClick={() => setActiveSubTab('insights')}
        >
          Insights {gapCount > 0 && <Badge bg="danger" className="ms-1">{gapCount}</Badge>}
        </Button>
        {activeSubTab === 'articles' && (
          <Button
            className="ms-auto"
            size="sm"
            style={{ background: '#e85d04', border: 'none', fontWeight: 700 }}
            onClick={() => setIsCreating(true)}
          >
            + New Article
          </Button>
        )}
        {activeSubTab === 'categories' && (
          <Button
            className="ms-auto"
            size="sm"
            style={{ background: '#e85d04', border: 'none', fontWeight: 700 }}
            onClick={() => setIsCreatingCategory(true)}
          >
            + New Category
          </Button>
        )}
      </div>

      {activeSubTab === 'articles' && (
        <>
          {/* Stats */}
          <div className="d-flex gap-3 mb-3">
            <div className="bg-white border rounded p-3 flex-fill text-center">
              <div style={{ fontSize: '22px', fontWeight: 800 }}>{pagination.total}</div>
              <small className="text-muted text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.07em' }}>Total Articles</small>
            </div>
            <div className="bg-white border rounded p-3 flex-fill text-center">
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#e85d04' }}>{adminCategories.length}</div>
              <small className="text-muted text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.07em' }}>Categories</small>
            </div>
            <div className="bg-white border rounded p-3 flex-fill text-center">
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#5a7abf' }}>{adminQuestions.length}</div>
              <small className="text-muted text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.07em' }}>Questions Logged</small>
            </div>
          </div>

          {/* Filters */}
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search articles…"
              aria-label="Search articles"
              value={searchFilter}
              onChange={e => { setSearchFilter(e.target.value); setPage(1); }}
              style={{ maxWidth: '300px' }}
            />
            <Form.Select
              size="sm"
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
              aria-label="Filter by category"
              style={{ maxWidth: '250px' }}
            >
              <option value="">All Categories</option>
              {categoryList.map(c => (
                <option key={c.category_id} value={c.category_id}>{indentedLabel(c)}</option>
              ))}
            </Form.Select>
            <small className="text-muted align-self-center">{pagination.total} shown</small>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" style={{ color: '#e85d04' }} /></div>
          ) : (
            <div className="bg-white border rounded overflow-hidden">
              <Table hover responsive className="mb-0" style={{ fontSize: '13px' }}>
                <thead className="bg-light">
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Views</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map(a => (
                    <tr key={a.article_id}>
                      <td>
                        <div className="fw-semibold">{a.title}</div>
                        {a.subtitle && <small className="text-muted">{a.subtitle.slice(0, 80)}{a.subtitle.length > 80 ? '…' : ''}</small>}
                      </td>
                      <td><small>{a.category_icon} {a.category}</small></td>
                      <td><small className="text-muted">{a.view_count}</small></td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="outline-secondary" size="sm" onClick={async () => {
                            await dispatch(fetchArticleById(a.article_id));
                            setEditingArticle(a);
                          }}>Edit</Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(a.article_id)}>Del</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {articles.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-muted py-4">No articles found.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <span className="align-self-center text-muted" style={{ fontSize: '13px' }}>Page {page} of {pagination.totalPages}</span>
              <Button variant="outline-secondary" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}
        </>
      )}

      {activeSubTab === 'categories' && (
        <div className="bg-white border rounded overflow-hidden">
          <Table hover responsive className="mb-0" style={{ fontSize: '13px' }}>
            <thead className="bg-light">
              <tr>
                <th style={{ width: '60px' }}>Icon</th>
                <th>Name</th>
                <th>Slug</th>
                <th style={{ width: '90px' }}>Articles</th>
                <th style={{ width: '90px' }}>Order</th>
                <th style={{ width: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categoryList.map(c => {
                const isActive = isActiveNode(c);
                const published = c.article_count ?? 0;
                const deleted = c.unpublished_count ?? 0;
                return (
                  <tr key={c.category_id} className={c.effectively_hidden ? 'text-muted' : undefined}>
                    <td style={{ fontSize: '18px' }}>{c.icon || '—'}</td>
                    <td className="fw-semibold" style={{ paddingLeft: `${8 + c.depth * 20}px` }}>
                      {c.depth > 0 && (
                        <>
                          <span className="text-muted me-1" aria-hidden="true">↳</span>
                          <span className="visually-hidden">Sub-category, level {c.depth + 1}: </span>
                        </>
                      )}
                      {c.name}
                      {!isActive && <Badge bg="secondary" className="ms-2">Hidden</Badge>}
                      {isActive && c.ancestor_hidden && (
                        <Badge bg="light" text="dark" className="ms-2 border">Hidden (parent)</Badge>
                      )}
                      {c.depth === 0 && (
                        <div className="text-muted fw-normal" style={{ fontSize: '11px' }}>
                          Visible to: {audienceLabel(c)}
                        </div>
                      )}
                    </td>
                    <td><small className="text-muted">{c.slug}</small></td>
                    <td>
                      <small className="text-muted">
                        {published}
                        {c.total_count > published && ` (${c.total_count} incl. sub-categories)`}
                        {deleted > 0 && ` · ${deleted} deleted`}
                      </small>
                    </td>
                    <td><small className="text-muted">{c.sort_order ?? 0}</small></td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setEditingCategory(c)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant={isActive ? 'outline-secondary' : 'outline-success'}
                          size="sm"
                          onClick={() => handleToggleActive(c)}
                          title={
                            isActive
                              ? 'Hide from the public page (nothing is deleted)'
                              : c.ancestor_hidden
                                ? 'Show (it stays hidden until its parent is shown too)'
                                : 'Show on the public page'
                          }
                        >
                          {isActive ? 'Hide' : 'Show'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {categoryList.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">No categories yet.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {activeSubTab === 'insights' && (
        <QuestionInsights
          questions={adminQuestions}
          conversations={adminConversations}
          onCreateFromQuestion={(question) => {
            setPrefillTitle(question);
            setIsCreating(true);
          }}
        />
      )}

      {/* Editor modal */}
      {(isCreating || editingArticle) && (
        <ArticleEditorModal
          article={isCreating ? null : (currentArticle || editingArticle)}
          prefillTitle={prefillTitle}
          categories={adminCategories}
          show={true}
          onClose={() => { setEditingArticle(null); setIsCreating(false); setPrefillTitle(''); dispatch(clearCurrentArticle()); }}
          onSaved={handleSaved}
        />
      )}

      {(isCreatingCategory || editingCategory) && (
        <CategoryEditorModal
          category={isCreatingCategory ? null : editingCategory}
          categories={adminCategories}
          show={true}
          onClose={() => { setEditingCategory(null); setIsCreatingCategory(false); }}
          onSaved={handleCategorySaved}
        />
      )}
    </div>
  );
};

export default AdminTab;
